import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, RefreshCw } from "lucide-react";
import { useLang } from "../context/LangContext";
import { notificationsAPI } from "../api";
import { markAllRead } from "../utils/notificationsRead";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

interface NotificationItem {
  id: number;
  title: string;
  text: string;
  image_url: string | null;
  createdAt: string;
}

const NotificationsPage: React.FC = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    notificationsAPI
      .getAll()
      .then((res) => {
        const list = res.data || [];
        setItems(list);
        markAllRead(list.map((n: NotificationItem) => n.id));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: 52,
        }}>
        <div
          style={{
            maxWidth: 520,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--muted)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
              }}>
              <ArrowLeft size={16} /> {t("back")}
            </button>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              {t("notificationsPageTitle")}
            </span>
          </div>
          <button
            onClick={load}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      <div
        style={{ maxWidth: 520, margin: "0 auto", padding: "68px 16px 32px" }}>
        {loading ?
          <p style={{ color: "var(--muted)", fontSize: 13, textAlign: "center" }}>
            {t("loading")}
          </p>
        : items.length === 0 ?
          <div
            style={{
              textAlign: "center",
              padding: "60px 16px",
              color: "var(--muted)",
            }}>
            <Bell size={36} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
            <p style={{ fontSize: 14 }}>{t("noNotifications")}</p>
          </div>
        : <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {items.map((n) => (
              <div
                key={n.id}
                className="fade-up"
                style={{
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 14,
                  padding: 16,
                  display: "flex",
                  gap: 12,
                }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    overflow: "hidden",
                    flexShrink: 0,
                    background: "var(--card2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                  {n.image_url ?
                    <img
                      src={`${IMG_BASE}/${n.image_url}`}
                      alt={n.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  : <Bell size={18} color="var(--muted)" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 14,
                      marginBottom: 4,
                    }}>
                    {n.title}
                  </p>
                  <p
                    style={{
                      color: "var(--muted)",
                      fontSize: 13,
                      lineHeight: 1.5,
                      marginBottom: 6,
                    }}>
                    {n.text}
                  </p>
                  <span style={{ color: "#475569", fontSize: 11 }}>
                    {new Date(n.createdAt).toLocaleString("uz-UZ")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
};

export default NotificationsPage;
